import { Slide, TableCell } from '@features/powerpoint';
import { contentSlideHeader, generateTable } from '../../utils';
import { ValueInput, formatters } from '@utils/formattersV2';
import {
  ChannelGrouping,
  Lci3SalesMetrics,
} from '@components/Dashboard/dashboardSlice/schemas/lci3Reports';
import { formatKeyTakeawaysText } from './key-takeaways-text';
import { parsedTakeaways } from '@features/lci-report/domain/key-takeaways';
import { NEWLINE_REGEX } from '../../domain/key-takeaways';

export const SUMMARY_TABLE_ROW_KEYS = [
  'incrementalSalesLift',
  'confidenceLevel',
  'incrementalSales',
  'totalProjectedSales',
  'exposedSalesRate',
  'controlSalesRate',
  'exposedTransactionSize',
  'controlTransactionSize',
  'transactionSizeLift',
  'exposedTransactionRate',
  'controlTransactionRate',
  'transactionRateLift',
  'exposedPenetration',
  'controlPenetration',
  'penetrationLift',
] as const;

export type SummaryTableRowKey = (typeof SUMMARY_TABLE_ROW_KEYS)[number];

type SummaryTableSlideProps = {
  slideMaster: string;
  overallSalesMetrics: Lci3SalesMetrics;
  keyTakeaways: string;
  rsaOverallResults: string;
  excludedRows?: SummaryTableRowKey[];
};

import { unifiedPlatformColorPalette } from '@utils/unifiedPlatformColorPalette';

export const TABLE_HEADER_OPTIONS = {
  color: unifiedPlatformColorPalette.inmarketGreen,
  bold: true,
} as const;

const summaryTableColumnLabels = [
  {
    text: '',
    options: TABLE_HEADER_OPTIONS,
  },
  {
    text: 'Combined',
    options: TABLE_HEADER_OPTIONS,
  },
  {
    text: 'In-Store',
    options: TABLE_HEADER_OPTIONS,
  },
  {
    text: 'Online',
    options: TABLE_HEADER_OPTIONS,
  },
] as const satisfies TableCell[];

const getLiftRowFromChannelGrouping = (
  label: string,
  grouping: ChannelGrouping,
  lift: ChannelGrouping,
  formatter: (value: ValueInput, lift: ValueInput) => string
): TableCell[] => {
  return [
    { text: label },
    { text: formatter(grouping.total, lift.total) },
    { text: formatter(grouping.inStore, lift.inStore) },
    { text: formatter(grouping.online, lift.online) },
  ];
};

const getRowFromChannelGrouping = (
  label: string,
  grouping: ChannelGrouping,
  formatter: (value: ValueInput) => string
): TableCell[] => {
  return [
    { text: label },
    { text: formatter(grouping.total) },
    { text: formatter(grouping.inStore) },
    { text: formatter(grouping.online) },
  ];
};

const subdimensionRows = (
  overallSalesMetrics: Lci3SalesMetrics,
  excludedRows: SummaryTableRowKey[] = []
): TableCell[][] => {
  const incrementalSalesLift = getRowFromChannelGrouping(
    'Incremental Sales Lift',
    overallSalesMetrics.incrementalSalesLift,
    value => formatters.lift(value)
  );
  const confidenceLevel = getLiftRowFromChannelGrouping(
    'Confidence Level',
    overallSalesMetrics.confidenceLevel,
    overallSalesMetrics.incrementalSalesLift,
    (value, lift) => formatters.confidenceLevel(value, lift)
  );

  const incrementalSales = getRowFromChannelGrouping(
    'Incremental Sales',
    overallSalesMetrics.incrementalSales,
    value => formatters.currency(value, { full: true, decimals: 0 })
  );

  const totalProjectedSales = getRowFromChannelGrouping(
    'Total Sales',
    overallSalesMetrics.totalProjectedSales,
    value => formatters.currency(value, { full: true, decimals: 0 })
  );

  const exposedSalesRate = getRowFromChannelGrouping(
    'Exposed Sales Rate',
    overallSalesMetrics.exposedSalesRate,
    value => formatters.currency(value, { full: true, decimals: 2 })
  );

  const controlSalesRate = getRowFromChannelGrouping(
    'Control Sales Rate',
    overallSalesMetrics.controlSalesRate,
    value => formatters.currency(value, { full: true, decimals: 2 })
  );

  const exposedTransactionSize = getRowFromChannelGrouping(
    'Exposed Transaction Size',
    overallSalesMetrics.exposedTransactionSize,
    value => formatters.currency(value)
  );

  const controlTransactionSize = getRowFromChannelGrouping(
    'Control Transaction Size',
    overallSalesMetrics.controlTransactionSize,
    value => formatters.currency(value)
  );

  const transactionSizeLift = getRowFromChannelGrouping(
    'Transaction Size Lift',
    overallSalesMetrics.transactionSizeLift,
    value => formatters.percentage(value)
  );

  const exposedTransactionRate = getRowFromChannelGrouping(
    'Exposed Transaction Rate',
    overallSalesMetrics.exposedTransactionRate,
    value => formatters.number(value, { decimals: 1 })
  );

  const controlTransactionRate = getRowFromChannelGrouping(
    'Control Transaction Rate',
    overallSalesMetrics.controlTransactionRate,
    value => formatters.number(value, { decimals: 1 })
  );

  const transactionRateLift = getRowFromChannelGrouping(
    'Transaction Rate Lift',
    overallSalesMetrics.transactionRateLift,
    value => formatters.percentage(value, { decimals: 1 })
  );

  const exposedPenetration = getRowFromChannelGrouping(
    'Exposed Penetration',
    overallSalesMetrics.exposedPenetration,
    value => formatters.percentage(value, { decimals: 1 })
  );

  const controlPenetration = getRowFromChannelGrouping(
    'Control Penetration',
    overallSalesMetrics.controlPenetration,
    value => formatters.percentage(value, { decimals: 1 })
  );

  const penetrationLift = getRowFromChannelGrouping(
    'Penetration Lift',
    overallSalesMetrics.penetrationLift,
    value => formatters.percentage(value, { decimals: 1 })
  );

  const rows: Record<SummaryTableRowKey, TableCell[]> = {
    incrementalSalesLift,
    confidenceLevel,
    incrementalSales,
    totalProjectedSales,
    exposedSalesRate,
    controlSalesRate,
    exposedTransactionSize,
    controlTransactionSize,
    transactionSizeLift,
    exposedTransactionRate,
    controlTransactionRate,
    transactionRateLift,
    exposedPenetration,
    controlPenetration,
    penetrationLift,
  };

  return SUMMARY_TABLE_ROW_KEYS.filter(key => !excludedRows.includes(key)).map(
    key => rows[key]
  );
};

export const salesLiftSummaryTableSlide = (
  props: SummaryTableSlideProps
): Slide => {
  return {
    slideMaster: props.slideMaster,
    tables: [
      generateTable(
        summaryTableColumnLabels,
        subdimensionRows(props.overallSalesMetrics, props.excludedRows),
        { rowH: 0.3, w: 7.11 }
      ),
    ],
    texts: [
      contentSlideHeader('Summary Table - Sales Lift'),
      {
        text: parsedTakeaways(props.rsaOverallResults.split(NEWLINE_REGEX)),
        options: {
          h: 3.28,
          w: 4.67,
          x: 8.49,
          y: 1.06,
          valign: 'top',
          paraSpaceAfter: 12,
          fontSize: 14,
        },
      },
      ...formatKeyTakeawaysText(
        props.keyTakeaways,
        { x: 8.49, y: 4.34 },
        { w: 4.41, x: 8.49, y: 4.74 }
      ),
    ],
  };
};
